/* Copyright (c) 2017 Backpack Health LLC */

/* global window, cordova */
import React from 'react';
import { Redirect } from 'react-router-dom';
import contentDisposition from 'content-disposition';
import * as _ from 'lodash';

import './FileView.scss';

import FileUpload from './FileUpload';
import FileDetail from './FileDetail';
import FilePreview from './FilePreview';
import DownloadFileProgress from './components/DownloadFileProgress';
import { FileSummaryFileView } from './components/FileInformation';
import AppNavFilePreview from './components/AppNavFilePreview';
import BottomDrawer, { BottomDrawerProvider } from './components/BottomDrawer';

import { Card } from '../../../bphComponents/bundle';

import FileService from '../../../apiServices/FileService';
import {
  isMobileApplication,
  mobileShareFile,
  isMobileOffline,
  alertOfflineMode,
} from '../../../utilities/MobileUtil';
import { withIntl } from '../../../utilities/localizedUtil';

import { useFileForActiveProfile } from '../../hooks/useFile';
import { useAttachmentsForFile } from '../../hooks/useAttachments';
import { useNavigate } from '../../hooks/useNavigate';
import { useActiveProfile } from '../../hooks/useProfile';
import { usePendingDataFileUpload } from '../../hooks/usePendingData';

import RouteNames from '../../appAuthorized/RouteNames';
import FileModel from '../../../models/FileModel';
import ProfileModel from '../../../models/ProfileModel';
import { useActiveUser } from '../../hooks/useUser';
import { useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import AppUtils from '../../../utilities/AppUtils';

function getExpandedState(displayMode) {
  switch (displayMode) {
    case 'upload':
      return 'partial';
    case 'detail':
      return 'fullscreen';
    case 'preview':
    default:
      return 'menu';
  }
}

function hasHeaderMenu(displayMode) {
  switch (displayMode) {
    case 'upload':
      return false;
    case 'detail':
    case 'preview':
    default:
      return true;
  }
}

function extractFileName(url) {
  const reg = /content-disposition=(.+)&/g;
  const data = reg.exec(url);
  const content = unescape(decodeURI(data[1]));
  const parse = contentDisposition.parse(content);
  const filename = _.get(parse, 'parameters.filename', null);
  return filename;
}

const FileDetailSummary = (props) => {
  const { onClick, onMount, file } = props;
  const elRef = React.useRef(null);
  const [{ attachments }] = useAttachmentsForFile(file.data.id);

  React.useEffect(() => {
    onMount(elRef.current);
  }, []);

  return (
    <div
      ref={elRef}
      className="cmp-fileview-previewinfo"
      onClick={onClick}
    >
      <Card.CardShape className="profile-background-color-accent" style={{ width: '100%' }} isFlat={true}>
        <Card.CardContent>
          <FileSummaryFileView
            file={file}
            attachments={attachments}
          />
        </Card.CardContent>
      </Card.CardShape>
    </div>
  );
};

const FileViewContent = (props) => {
  const { profileId, rawFile, displayMode, setDisplayMode, file, onSetSummaryHeight, attachmentType, parentId } = props;

  const [{ preferredLanguageCode }] = useActiveUser();

  const setToPreviewMode = () => {
    setDisplayMode('preview');
  };

  switch (displayMode) {
    case 'upload':
      return (
        <FileUpload
          file={file}
          rawFile={rawFile}
          profileId={profileId}
          attachmentType={attachmentType}
          parentId={parentId}
          onSuccess={setToPreviewMode}
        />
      );
    case 'detail':
      return (
        <FileDetail
          key="file-detail"
          profileId={profileId}
          file={file}
          onClose={setToPreviewMode}
          languageCode={preferredLanguageCode}
        />
      );
    case 'preview':
    default:
      return (
        <FileDetailSummary
          key="file-preview"
          onClick={() => {
            setDisplayMode('detail');
          }}
          onMount={(el) => {
            onSetSummaryHeight(el.offsetHeight);
          }}
          file={file}
        />
      );
  }
};

function getParamsFromUrl(props) {
  const isUploading = props.match.params.fileId === 'upload';
  const fileId = isUploading ? null : Number(props.match.params.fileId);
  const { attachmentType, parentId } = AppUtils.getQueryParams(props.location.search);

  return {
    attachmentType,
    parentId: Number(parentId),
    fileId,
    isUploading,
  };
}

function existingFileReducer(state, action) {
  switch (action.type) {
    case 'UPDATE':
      return { ...state, ...action.payload };
    default:
      throw new Error();
  }
}

function useNewFile(fileId) {

  const [state, setState] = React.useState({
    fileDownloadUrl: '',
    filePreviewUrl: '',
  });

  const [{ rawFile, newFile }] = usePendingDataFileUpload(fileId);

  React.useEffect(() => {
    if (rawFile) {
      const url = URL.createObjectURL(rawFile);
      setState({
        fileDownloadUrl: url,
        filePreviewUrl: url,
      });
    }
  }, [rawFile]);

  return [{
    rawFile,
    newFile,
    fileDownloadUrl: state.fileDownloadUrl,
    filePreviewUrl: state.filePreviewUrl,
    hasNewFile: Boolean(rawFile),
  }];
}

const initialState = {
  isDownloading: false,
  downloadDone: false,
  downloadError: false,
  fileDownloadUrl: '',
  filePreviewUrl: '',
  fileDataOriginal: null,
};
function useExistingFile(fileId, profileId) {
  const [, fileActions] = useFileForActiveProfile(fileId);
  const file = fileActions.instantiateFile();
  const downloadTimeout = React.useRef(null);

  const [state, dispatch] = React.useReducer(existingFileReducer, initialState);
  const setState = (d) => {
    dispatch({
      type: 'UPDATE',
      payload: d,
    });
  };

  function getImagePreview(fId) {
    FileService.getDownloadUrl(
      profileId,
      fId,
      (apiError, url) => {
        if (file.isImage()) {
          setState({
            fileDownloadUrl: url,
            filePreviewUrl: url,
            rawFile: null,
          });
        } else {
          setState({
            fileDownloadUrl: url,
            rawFile: null,
          });
        }
      },
    );

    if (!file.isImage()) {
      FileService.getPreviewUrl(
        profileId,
        fId,
        (apiError, url) => {
          setState({
            filePreviewUrl: url,
          });
        },
      );
    }
  }

  function mobileDownloadToLocal(fileURL) {
    setState({
      isDownloading: true,
      downloadError: false,
      downloadDone: false,
    });
    if (isMobileOffline()) {
      alertOfflineMode();
      setState({
        isDownloading: false,
        downloadError: true,
        downloadDone: false,
      });
      return;
    }

    const fail = () => {
      setState({ isDownloading: false, downloadError: false });
    };
    const success = () => {
      setState({ isDownloading: false, downloadDone: true });
      downloadTimeout.current = setTimeout(() => {
        setState({ isDownloading: false, downloadDone: false, downloadError: false });
        clearTimeout(downloadTimeout.current);
        downloadTimeout.current = null;
      }, 1500);
    };
    const filename = extractFileName(fileURL);
    try {
      cordova.plugins.DownloadManager.download(filename, fileURL, file.getMimeType(), success, fail);
    } catch (e) {
      fail();
    }
  }

  React.useEffect(() => {
    if (fileId) {
      getImagePreview(fileId);
    }
  }, [fileId]);

  React.useEffect(() => {
    setState({ fileDataOriginal: file.data });
    // window.scroll(0, 0);

    return () => {
      if (downloadTimeout.current) {
        clearTimeout(downloadTimeout.current);
        downloadTimeout.current = null;
      }
    };
  }, []);

  return [
    {
      ...state,
      file,
      isBusy: state.isDownloading,
    },
    {
      mobileDownloadToLocal,
    },
  ];
}

function useFileManagment(props, profileId) {
  const { fileId, isUploading } = getParamsFromUrl(props);
  const { intl } = props;

  const [newFileState] = useNewFile(fileId);
  const [existingFileState, existingFileActions] = useExistingFile(fileId, profileId);
  let file = null;

  const previewData: any = {
    fileDownloadUrl: newFileState.fileDownloadUrl,
    filePreviewUrl: newFileState.filePreviewUrl,
  };

  if (fileId) {
    file = existingFileState.file;

    if (existingFileState.filePreviewUrl) {
      previewData.fileDownloadUrl = existingFileState.fileDownloadUrl;
      previewData.filePreviewUrl = existingFileState.filePreviewUrl;
    }
  } else {
    file = newFileState.newFile;
  }

  const downloadActions = isMobileApplication() ? {
    download: existingFileActions.mobileDownloadToLocal,
    shareFile: () => {
      const filename = extractFileName(existingFileState.fileDownloadUrl);
      const dFile = new FileModel(file.data);
      dFile.setName(filename);
      mobileShareFile(dFile, existingFileState.fileDownloadUrl, intl);
    },
  } : {
    download: (fileUrl) => { window.location = fileUrl; },
    shareFile: null,
  };

  return {
    data: {
      isDownloading: existingFileState.isDownloading,
      downloadDone: existingFileState.downloadDone,
      downloadError: existingFileState.downloadError,
      fileDownloadUrl: previewData.fileDownloadUrl,
      filePreviewUrl: previewData.filePreviewUrl,
      file,
      originalFile: existingFileState.originalFile,
      isFileNew: isUploading,
      isFileMissing: !newFileState.hasNewFile && isUploading,
      isBusy: existingFileState.isBusy,
      isLoaded: isUploading || Boolean(file.data.name),
      isInvalid: false,
      rawFile: newFileState.rawFile,
    },
    actions: downloadActions,
  };
}

const FileView = (props) => {
  const { isUploading, attachmentType, parentId } = getParamsFromUrl(props);

  const [activeProfileData] = useActiveProfile();
  const activeProfile = new ProfileModel(activeProfileData.data);
  const profileId = activeProfile.data.id;
  const [scData] = useShareCardListForActiveProfile();
  const isAttachment = Boolean(attachmentType && parentId);
  const [bottomOffset, setBottomOffset] = React.useState(0);
  const [displayMode, setDisplayMode] = React.useState(isUploading ? 'upload' : 'preview');
  const fileManagementData = useFileManagment(props, profileId);
  const fileManagementState = fileManagementData.data;
  const fileManagementActions = fileManagementData.actions;

  const isLoaded = fileManagementState.isLoaded && scData.isLoaded;

  const [, navActions] = useNavigate();

  if (fileManagementState.isFileMissing) {
    return <Redirect to={RouteNames.myBackpackFiles()} />;
  }
  if (!isLoaded) {
    return null;
  }
  return (
    <div style={{ height: '100%' }}>
      <div className="cmp-fileview__content-area">
        {
          hasHeaderMenu(displayMode)
          ? (
              <AppNavFilePreview
                fileDownloadUrl={fileManagementState.fileDownloadUrl}
                download={{
                  disabled: fileManagementState.isBusy,
                  onClick: fileManagementActions.download,
                }}
                share={{
                  disabled: fileManagementState.isBusy,
                  onClick: fileManagementActions.shareFile,
                }}
                done={{
                  disabled: fileManagementState.isBusy || fileManagementState.isInvalid,
                  onClick: () => {
                    if (isAttachment) {
                      navActions.goBack(RouteNames.myBackpackFiles());
                    } else if (fileManagementState.isFileNew) {
                      navActions.goTo(RouteNames.myBackpackFiles());
                    } else {
                      navActions.goBack(RouteNames.myBackpackFiles());
                    }
                  },
                }}
              />
          ) : null
        }

        <div
          className="cmp-filepreview__preview-mask"
          style={displayMode === 'preview' ? null : {
            overflow: 'hidden',
            height: `${window.innerHeight - bottomOffset - 30}px`,
          }}
        >
          <FilePreview
            attemptDownload={(fileUrl) => {
              window.location = fileUrl;
            }}
            downloadUrl={fileManagementState.fileDownloadUrl}
            previewUrl={fileManagementState.filePreviewUrl}
            file={fileManagementState.file}
            shareFile={fileManagementActions.shareFile}
            profile={activeProfile}
          />
        </div>

        <div className="__placeholder" style={{ height: bottomOffset }} />
      </div>

      <DownloadFileProgress
        isVisible={isMobileApplication()}
        isDownloading={fileManagementState.isDownloading}
        isDone={fileManagementState.downloadDone}
        isError={fileManagementState.downloadError}
      />

      <BottomDrawerProvider>
        <BottomDrawer
          expandedState={getExpandedState(displayMode)}
          className={displayMode === 'preview' ? 'profile-background-color' : 'profile-background-color-accent'}
        >
          <FileViewContent
            profileId={profileId}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            file={fileManagementState.file}
            attachmentType={attachmentType}
            parentId={parentId}
            rawFile={fileManagementState.rawFile}
            onSetSummaryHeight={(val) => {
              setBottomOffset(val);
            }}
          />
        </BottomDrawer>
      </BottomDrawerProvider>
    </div>
  );
};

const IntlFileView = withIntl(FileView);

export default IntlFileView;
